import { isFormElement } from '../dropdownHelpers';
import { AddListener, Listener } from './modalDropdownTypes';
import {
  ReactElement,
  MutableRefObject,
  MouseEvent,
  createRef,
  Component,
} from 'react';
import styled from 'styled-components';
import { zIndex } from '@ardoq/design-tokens';
import { isMac } from '@ardoq/common-helpers';

type BackdropRenderProps = (
  addKeyboardListener: AddListener,
  removeKeyboardListener: AddListener
) => ReactElement<any>;

interface ModalBackdropProps {
  closeModal: VoidFunction;
  isKeepOpen?: boolean;
  ignoreFocusTrap?: boolean;
  ignoreKeyboardEvents?: boolean;
  clickNamespace?: string;
  children?: BackdropRenderProps;
}

const ModalBackdropDiv = styled.div`
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: ${zIndex.MODAL_BACKDROP};
  &:focus {
    outline: none;
  }
`;

const preventContextMenu = (event: React.MouseEvent<HTMLElement>) => {
  event.stopPropagation();
};

export class ModalBackdrop extends Component<ModalBackdropProps> {
  contentRef: MutableRefObject<HTMLDivElement | null> = createRef();
  backdropRef: MutableRefObject<HTMLDivElement | null> = createRef();
  mouseDownTarget: HTMLElement | null = null;
  listeners: Listener[] = [];
  skipCloseModal = false;

  onBackdropClick = (event: MouseEvent<HTMLElement>) => {
    // Chrome treats any mouseup as click, even if the targets between mouse
    // down and mouse up have changed.
    if (!this.isClick(event)) {
      return;
    }
    event.stopPropagation();
    const { isKeepOpen, closeModal } = this.props;
    if (!(isKeepOpen && this.isContentClick(event))) {
      closeModal();
    }
  };

  onBackdropMouseDown = (event: MouseEvent<HTMLElement>) => {
    this.mouseDownTarget = event.target as HTMLElement;
  };

  addKeyboardListener: AddListener = (listener: Listener) => {
    this.listeners.unshift(listener);
  };

  removeKeyboardListener: AddListener = (listener: Listener) => {
    this.listeners = this.listeners.filter(l => l !== listener);
  };

  onKeyboardDown = (event: KeyboardEvent) => {
    this.ensureFocusInBackdrop();

    // Custom handler to tell the function maybeCloseModal() to not close the modal
    if (event.key === 'f' && (isMac() ? event.metaKey : event.ctrlKey)) {
      this.skipCloseModal = true;
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'ArrowUp':
      case 'Down':
      case 'Left':
      case 'Right':
      case 'Up':
        if (isFormElement(event.target)) {
          break;
        }
      // else fall through
      case 'End':
      case 'Home':
      case 'PageDown':
      case 'PageUp':
      case 'Tab':
        event.preventDefault();
        break;
    }

    if (this.listeners.every(listener => listener(event))) {
      switch (event.key) {
        case 'Esc':
        case 'Escape':
          this.closeModal();
          event.preventDefault();
          event.stopPropagation();
          return;
      }
    }
  };

  closeModal = () => this.props.closeModal();

  maybeCloseModal = () => {
    if (this.skipCloseModal) this.skipCloseModal = false;
    else this.closeModal();
  };

  componentDidMount() {
    if (!this.props.ignoreFocusTrap) {
      this.ensureFocusInBackdrop();
    }
    window.addEventListener('resize', this.closeModal);
    window.addEventListener('blur', this.maybeCloseModal);
    if (!this.props.ignoreKeyboardEvents) {
      document.addEventListener('keydown', this.onKeyboardDown, true);
    }
  }

  componentWillUnmount() {
    this.listeners.length = 0;
    window.removeEventListener('resize', this.closeModal);
    window.removeEventListener('blur', this.maybeCloseModal);
    document.removeEventListener('keydown', this.onKeyboardDown, true);
  }

  render() {
    return (
      <ModalBackdropDiv
        ref={this.backdropRef}
        onClick={this.onBackdropClick}
        onMouseDown={this.onBackdropMouseDown}
        onContextMenu={preventContextMenu}
        tabIndex={1}
        data-click-namespace={this.props.clickNamespace}
      >
        <div ref={this.contentRef}>
          {this.props.children?.(
            this.addKeyboardListener,
            this.removeKeyboardListener
          )}
        </div>
      </ModalBackdropDiv>
    );
  }

  isClick(event: MouseEvent<HTMLElement>) {
    // Manually created events have the isTrusted flag set to false.
    const isClick = !event.isTrusted || event.target === this.mouseDownTarget;
    this.mouseDownTarget = null;
    return isClick;
  }

  isContentClick(event: MouseEvent<HTMLElement>) {
    return this.contentRef.current?.contains(event.target as Node);
  }

  ensureFocusInBackdrop() {
    if (
      this.backdropRef.current &&
      !this.backdropRef.current.contains(document.activeElement)
    ) {
      this.backdropRef.current.focus();
    }
  }
}
